import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { TItemSelector } from 'libs/design-system/src/types/item_selector/item_selector';
import {
  FormatCurrencyForDisplay,
  ItemSelectorGroup,
  SkeletonBase,
  SkeletonLoader,
} from '@unified-checkout/design-system';

import type {
  IInstantSettlementItemSelector,
  ISettlementBalance,
  TBalanceType,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/instant-settlement';
import { HAVE_ONE_SETTLE_WAY } from '@app/dashboard/constants/balance_and_transfers/balance/modals';
import { useLangStore } from '@app/dashboard/store/lang';

import { formatAmountWithCurrency } from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';

import styles from './item-selector.module.scss';

const ItemSelector = ({
  isLoading,
  selectedBalanceType,
  handleBalanceTypeChange,
  balances = [],
}: IInstantSettlementItemSelector) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const handleItemClick = (
    value: TBalanceType,
    balance: ISettlementBalance
  ) => {
    handleBalanceTypeChange(value, balance);
  };

  const RenderSkeleton = () => {
    return (
      <div className={styles['instant-settlement__selector__skeleton']}>
        <SkeletonBase width="8rem" height="1.125rem" />
        <div
          className={styles['instant-settlement__selector__skeleton__boxes']}
        >
          {Array.from({ length: 2 }).map((_, index) => (
            <SkeletonLoader
              width="100%"
              noOfRows={3}
              type="bordered_box"
              height="8rem"
              key={`skeleton-loader-${index}`}
            />
          ))}
        </div>
      </div>
    );
  };

  const RenderItemContent = ({
    title,
    amount = 0,
    currency,
  }: {
    title: string;
    amount: number;
    currency: string;
  }) => {
    const isAvailable = !!amount;
    const amountWithCurrency = formatAmountWithCurrency({
      amount,
      currency,
      lang,
    });

    return (
      <div className={styles['instant-settlement__selector__item']}>
        <p className={styles['instant-settlement__selector__item__title']}>
          {title}
        </p>
        <p className={styles['instant-settlement__selector__item__subtitle']}>
          {t(
            `innerLayout.balanceTransfers.balance.instantSettlement.${
              isAvailable ? 'available' : 'notAvailable'
            }`
          )}
        </p>
        <p className={styles['instant-settlement__selector__item__amount']}>
          {FormatCurrencyForDisplay(amountWithCurrency)}
        </p>
      </div>
    );
  };

  const items: TItemSelector[] = balances.map((balance) => ({
    id: balance.balance_type,
    selected: selectedBalanceType === balance.balance_type,
    size: 'md',
    disabled: isLoading || !balance.balance,
    render: (
      <RenderItemContent
        title={balance.title}
        amount={balance.balance as number}
        currency={balance.currency || ''}
      />
    ),
    onClickHandler: () => handleItemClick(balance.balance_type, balance),
  }));

  useEffect(() => {
    if (balances.length === HAVE_ONE_SETTLE_WAY) {
      const currentBalance = balances[0];

      handleBalanceTypeChange(
        currentBalance.balance_type as TBalanceType,
        currentBalance
      );
    }
  }, [balances]);

  return (
    <div className={styles['instant-settlement__selector']}>
      {isLoading ? (
        <RenderSkeleton />
      ) : (
        <ItemSelectorGroup
          label={t(
            'innerLayout.balanceTransfers.balance.instantSettlement.chooseBalance'
          )}
          key="item-selector-group-instant-settlement"
          type="radio"
          orientation="horizontal"
          hasFirstItemSelected={false}
          items={items as TItemSelector[]}
        />
      )}
    </div>
  );
};

export default ItemSelector;
